import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  TextField,
  Typography,
} from "@mui/material";
import { Info } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import { COLORS } from "components";
import {
  ReminderFormValues,
  SendReminderModalProps,
} from "../sendReminderButton/types";
import {
  SendReminderCollectorsTable,
  SendReminderModalFooter,
  SendReminderModalHeader,
} from "./components";
import { COMMON_LABEL_STYLES } from "./styles";

export const SendReminderModal = ({
  isLoadingFormData,
  isSending,
  dataProviders,
  recipients,
  defaultSubject,
  defaultMessage,
  collectors,
  platformUsers,
  onClose,
  onSend,
  onDataProviderRemove,
  onRecipientsChange,
}: SendReminderModalProps) => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
  } = useForm<ReminderFormValues>({
    defaultValues: {
      subject: defaultSubject,
      helloMessage: defaultMessage.helloMessage,
      signatureMessage: defaultMessage.tableDescriptionMessage
        ? `${defaultMessage.tableDescriptionMessage}\n\n${defaultMessage.signatureMessage}`
        : defaultMessage.signatureMessage,
      manualEmail: "",
    },
  });

  const safeRecipients = recipients ?? [];

  const removeRecipient = (email: string) => {
    onRecipientsChange(safeRecipients.filter((r) => r.email !== email));
  };

  const handleManualKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter" && e.key !== ",") return;
    e.preventDefault();
    const email = (e.target as HTMLInputElement).value.trim().replace(/,$/, "");
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("manualEmail", {
        message: "Please enter a valid email address",
      });
      return;
    }
    if (safeRecipients.some((r) => r.email === email)) {
      setError("manualEmail", { message: "This email has already been added" });
      return;
    }
    clearErrors("manualEmail");
    const match = (platformUsers ?? []).find((u) => u.email === email);
    onRecipientsChange([...safeRecipients, match ?? { email }]);
    setValue("manualEmail", "");
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "780px",
          maxWidth: "95vw",
          borderRadius: "16px",
          boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        },
      }}
    >
      <SendReminderModalHeader onClose={onClose} />

      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5 }}>
        {isLoadingFormData ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Info banner */}
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                px: 1.75,
                py: 1.5,
                background: "#EFF6FF",
                border: "1px solid #BFDBFE",
                borderRadius: "10px",
                color: "#1E3A8A",
              }}
            >
              <Info
                sx={{
                  fontSize: 20,
                  color: "#1E3A8A",
                  flexShrink: 0,
                  mt: "1px",
                }}
              />
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "12.5px",
                    mb: 0.25,
                    color: "#1E3A8A",
                  }}
                >
                  How recipients work
                </Typography>
                <Typography
                  sx={{ fontSize: "12.5px", color: "#1E3A8A", lineHeight: 1.5 }}
                >
                  <strong>Assigned data providers</strong> (auto-added from
                  selected sites) receive a <strong>personalized email</strong>{" "}
                  listing only the sites/collectors they&apos;re responsible
                  for. Any <strong>recipient you add manually</strong> receives
                  the <strong>full summary</strong> with every selected site.
                </Typography>
              </Box>
            </Box>

            {/* Auto-assigned recipients */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Box sx={{ width: 92, flexShrink: 0, pt: 1 }}>
                <Typography
                  sx={{ ...COMMON_LABEL_STYLES, pt: 0, width: "auto" }}
                >
                  To
                </Typography>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "#94A3B8",
                    mt: 0.25,
                  }}
                >
                  (auto-assigned)
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                    p: 0.75,
                    border: "1px solid",
                    borderColor: "grey.300",
                    borderRadius: "8px",
                    minHeight: "42px",
                    backgroundColor: "#fff",
                    alignItems: "center",
                  }}
                >
                  {dataProviders.length === 0 ? (
                    <Typography
                      sx={{ fontSize: "12px", color: "#94A3B8", px: 0.5 }}
                    >
                      No data provider assigned to selected sites — use &quot;+
                      Add&quot; below
                    </Typography>
                  ) : (
                    dataProviders.map((p) => (
                      <Chip
                        key={p.userId}
                        label={
                          <Box
                            component="span"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                background: "rgba(12,74,110,.12)",
                                color: "#0C4A6E",
                                fontSize: "10px",
                                fontWeight: 700,
                                px: "6px",
                                py: "2px",
                                borderRadius: "4px",
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {`${p.assignedSitesCount} site${
                                p.assignedSitesCount !== 1 ? "s" : ""
                              }`}
                            </Box>
                            {`${p.firstName ?? ""} ${p.lastName ?? ""} <${
                              p.email
                            }>`}
                          </Box>
                        }
                        size="small"
                        onDelete={() => onDataProviderRemove(p.userId)}
                        sx={{
                          height: "30px",
                          backgroundColor: "#E0F2FE",
                          color: "#0C4A6E",
                          borderRadius: "999px",
                          border: "1px solid #BAE6FD",
                          "& .MuiChip-deleteIcon": {
                            color: "#0C4A6E",
                            "&:hover": { color: COLORS.black },
                          },
                        }}
                      />
                    ))
                  )}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mt: 0.75,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "999px",
                      background: "#0EA5E9",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{ fontSize: "11.5px", color: "text.secondary" }}
                  >
                    Each recipient only sees their assigned collectors in the
                    email table ·{" "}
                    <strong>
                      {dataProviders.length} recipient
                      {dataProviders.length !== 1 ? "s" : ""}
                    </strong>
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Manual additional recipients */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Box sx={{ width: 92, flexShrink: 0, pt: 1 }}>
                <Typography
                  sx={{ ...COMMON_LABEL_STYLES, pt: 0, width: "auto" }}
                >
                  + Add
                </Typography>
                <Typography
                  sx={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "#94A3B8",
                    mt: 0.25,
                  }}
                >
                  (optional)
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    minHeight: "51px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                    p: 0.75,
                    border: "1px dashed #FCD34D",
                    borderRadius: "8px",
                    backgroundColor: "#FFFBEB",
                    alignItems: "center",
                  }}
                >
                  {safeRecipients.map((r) => (
                    <Chip
                      key={r.email}
                      label={
                        <Box
                          component="span"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              background: "rgba(146,64,14,.15)",
                              color: "#92400E",
                              fontSize: "10px",
                              fontWeight: 700,
                              px: "6px",
                              py: "2px",
                              borderRadius: "4px",
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              whiteSpace: "nowrap",
                            }}
                          >
                            Full
                          </Box>
                          {r.email}
                        </Box>
                      }
                      size="small"
                      onDelete={() => removeRecipient(r.email)}
                      sx={{
                        height: "30px",
                        backgroundColor: "#FFF7E6",
                        color: "#92400E",
                        borderRadius: "999px",
                        border: "1px solid #FCD34D",
                        "& .MuiChip-deleteIcon": {
                          color: "#92400E",
                          "&:hover": { color: COLORS.black },
                        },
                      }}
                    />
                  ))}
                  <input
                    {...register("manualEmail", {
                      onChange: () => {
                        if (errors.manualEmail) clearErrors("manualEmail");
                      },
                    })}
                    onKeyDown={handleManualKeyDown}
                    placeholder="Type an email and press Enter…"
                    style={{
                      height: "37px",
                      border: "1px solid",
                      borderColor: "#DDDEE3",
                      borderRadius: "4px",
                      outline: "none",
                      fontFamily: "inherit",
                      fontSize: "13px",
                      padding: "4px 6px",
                      minWidth: "180px",
                      background: "white",
                      flex: 1,
                    }}
                  />
                </Box>
                {errors.manualEmail && (
                  <Typography
                    sx={{ fontSize: "11.5px", color: "error.main", mt: 0.5 }}
                  >
                    {errors.manualEmail.message}
                  </Typography>
                )}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    mt: 0.75,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "999px",
                      background: "#F59E0B",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{ fontSize: "11.5px", color: "text.secondary" }}
                  >
                    Anyone added here receives the{" "}
                    <strong>full table of all selected sites</strong> ·{" "}
                    <strong>
                      {safeRecipients.length} recipient
                      {safeRecipients.length !== 1 ? "s" : ""}
                    </strong>
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Typography sx={COMMON_LABEL_STYLES}>Subject</Typography>
              <Controller
                name="subject"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        height: "40px",
                        fontSize: "14px",
                      },
                    }}
                  />
                )}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Typography sx={COMMON_LABEL_STYLES}>Message</Typography>
              <Box
                sx={{
                  flex: 1,
                  border: "1px solid",
                  borderColor: "grey.300",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  overflow: "hidden",
                }}
              >
                <Controller
                  name="helloMessage"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      multiline
                      minRows={2}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 0,
                          "& fieldset": { border: "none" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "14px",
                          lineHeight: 1.6,
                        },
                        "& .MuiInputBase-root": { overflow: "hidden" },
                      }}
                    />
                  )}
                />

                {collectors.length > 0 && (
                  <Box sx={{ px: 2, pb: 2 }}>
                    <SendReminderCollectorsTable collectors={collectors} />
                  </Box>
                )}

                <Controller
                  name="signatureMessage"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      multiline
                      minRows={2}
                      fullWidth
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 0,
                          "& fieldset": { border: "none" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "14px",
                          lineHeight: 1.6,
                        },
                        "& .MuiInputBase-root": { overflow: "hidden" },
                      }}
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <SendReminderModalFooter
        recipientCount={dataProviders.length + safeRecipients.length}
        isSending={isSending}
        onClose={onClose}
        onSend={handleSubmit(onSend)}
      />
    </Dialog>
  );
};
